import { ReactNode, useState, useEffect } from 'react';
import type { AuthUser } from 'aws-amplify/auth';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { useAdminStatus } from './RequireAdmin';
import {
  ShoppingBag,
  Package,
  Shield,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Mail,
  ExternalLink,
} from 'lucide-react';

interface LayoutProps {
  user: AuthUser | undefined;
  signOut: (() => void) | undefined;
  children: ReactNode;
}

export default function Layout({ user, signOut, children }: LayoutProps) {
  const { isAdmin, isLoading: adminLoading } = useAdminStatus();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { to: '/', label: 'Catalog', icon: ShoppingBag },
    { to: '/orders', label: 'My Orders', icon: Package },
    { to: '/display-cases', label: 'Display Cases', icon: Package },
  ];

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-workshop flex flex-col">
      {/* Header */}
      <header
        className={`
          sticky top-0 z-40 transition-all duration-300
          ${scrolled
            ? 'bg-workshop-surface/95 backdrop-blur-md shadow-lg border-b border-white/[0.08]'
            : 'bg-workshop-surface border-b border-white/[0.06]'
          }
        `}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <motion.div
                whileHover={{ scale: 1.02 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <h1 className="text-accent text-xl font-display tracking-extra-wide group-hover:text-accent-light transition-colors">
                  TOP DOG BRAND
                </h1>
              </motion.div>
              <span className="hidden sm:inline-flex px-2 py-0.5 text-[10px] uppercase tracking-wider text-text-muted border border-white/10 rounded-sm bg-white/[0.03]">
                Wholesale
              </span>
            </Link>

            {/* Desktop Navigation */}
            {user && (
              <nav className="hidden md:flex items-center gap-1">
                {navLinks.map((link) => {
                  const active = isActive(link.to);
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`
                        relative px-3 py-2 text-xs uppercase tracking-wide rounded-sm
                        transition-all duration-200 flex items-center gap-2
                        ${active
                          ? 'text-accent'
                          : 'text-text-muted hover:text-cream hover:bg-white/[0.04]'
                        }
                      `}
                    >
                      <link.icon className="w-3.5 h-3.5" />
                      {link.label}
                      {active && (
                        <motion.div
                          layoutId="nav-indicator"
                          className="absolute bottom-0 left-2 right-2 h-0.5 bg-accent rounded-full"
                          transition={{ type: 'spring', stiffness: 500, damping: 35 }}
                        />
                      )}
                    </Link>
                  );
                })}

                {!adminLoading && isAdmin && (
                  <Link
                    to="/admin"
                    className={`
                      px-3 py-1.5 text-xs uppercase tracking-wide rounded-sm
                      flex items-center gap-2 ml-2 transition-all duration-200
                      ${location.pathname.startsWith('/admin')
                        ? 'bg-accent text-workshop'
                        : 'text-accent border border-accent/40 hover:bg-accent hover:text-workshop'
                      }
                    `}
                  >
                    <Shield className="w-3.5 h-3.5" />
                    Admin
                  </Link>
                )}

                {/* Divider */}
                <div className="w-px h-6 bg-white/10 mx-2" />

                {/* User section */}
                <div className="flex items-center gap-3">
                  <span className="text-text-muted text-xs hidden lg:inline truncate max-w-[160px]">
                    {user.signInDetails?.loginId || 'Welcome'}
                  </span>
                  <button
                    onClick={signOut}
                    className="p-2 text-text-muted hover:text-red-400 rounded-sm hover:bg-red-400/10 transition-all duration-200"
                    title="Sign Out"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </div>
              </nav>
            )}

            {/* Mobile hamburger */}
            {user && (
              <button
                onClick={() => setMobileMenuOpen((prev) => !prev)}
                className="md:hidden p-2 text-text-muted hover:text-accent rounded-sm transition-colors"
                aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                <AnimatePresence mode="wait">
                  {mobileMenuOpen ? (
                    <motion.div
                      key="close"
                      initial={{ rotate: -90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: 90, opacity: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      <X className="w-5 h-5" />
                    </motion.div>
                  ) : (
                    <motion.div
                      key="menu"
                      initial={{ rotate: 90, opacity: 0 }}
                      animate={{ rotate: 0, opacity: 1 }}
                      exit={{ rotate: -90, opacity: 0 }}
                      transition={{ duration: 0.15 }}
                    >
                      <Menu className="w-5 h-5" />
                    </motion.div>
                  )}
                </AnimatePresence>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Mobile Nav Panel */}
      <AnimatePresence>
        {mobileMenuOpen && user && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: [0.4, 0, 0.2, 1] }}
            className="md:hidden bg-workshop-surface/95 backdrop-blur-md border-b border-white/[0.06] overflow-hidden z-30 sticky top-16"
          >
            <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col">
              {navLinks.map((link, i) => {
                const active = isActive(link.to);
                return (
                  <motion.div
                    key={link.to}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: i * 0.05 }}
                  >
                    <Link
                      to={link.to}
                      className={`
                        flex items-center justify-between px-4 py-3 rounded-sm
                        text-sm uppercase tracking-wide transition-colors
                        ${active
                          ? 'text-accent bg-accent/10'
                          : 'text-text-muted hover:text-cream hover:bg-white/[0.04]'
                        }
                      `}
                    >
                      <span className="flex items-center gap-3">
                        <link.icon className="w-4 h-4" />
                        {link.label}
                      </span>
                      <ChevronRight className="w-4 h-4 opacity-30" />
                    </Link>
                  </motion.div>
                );
              })}

              {!adminLoading && isAdmin && (
                <motion.div
                  initial={{ x: -20, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: 0.15 }}
                >
                  <Link
                    to="/admin"
                    className="flex items-center justify-between px-4 py-3 rounded-sm
                      text-sm uppercase tracking-wide text-accent
                      border border-accent/30 mt-2 hover:bg-accent hover:text-workshop transition-colors"
                  >
                    <span className="flex items-center gap-3">
                      <Shield className="w-4 h-4" />
                      Admin Panel
                    </span>
                    <ChevronRight className="w-4 h-4 opacity-40" />
                  </Link>
                </motion.div>
              )}

              <div className="border-t border-white/[0.06] mt-3 pt-3">
                <p className="px-4 py-1 text-text-muted text-xs truncate">
                  {user.signInDetails?.loginId || 'Welcome'}
                </p>
                <button
                  onClick={signOut}
                  className="w-full text-left flex items-center gap-3 px-4 py-3 text-sm uppercase tracking-wide
                    text-text-muted hover:text-red-400 hover:bg-red-400/5 rounded-sm transition-colors"
                >
                  <LogOut className="w-4 h-4" />
                  Sign Out
                </button>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1 w-full">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-workshop-surface border-t border-white/[0.06] mt-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-text-muted text-sm">
            <p className="flex items-center gap-1.5">
              &copy; {new Date().getFullYear()} Top Dog Brand. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <a
                href="mailto:wholesale@topdogbrand.com"
                className="flex items-center gap-1.5 hover:text-accent transition-colors"
              >
                <Mail className="w-3.5 h-3.5" />
                Contact Support
              </a>
              <a
                href="https://topdogbrand.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 hover:text-accent transition-colors"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                Main Site
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
